import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { confirmPopup } from "primereact/confirmpopup";

import FileUploadWithCustomTemplate from "./fileUploadeWithCustomTemplate";
import { AppDispatch, RootState } from "../../store";
import {
  deleteAttachmentFile,
  fetchAttachmentFiles,
  resetFileUploadState,
  resteDeleteFileState,
  uploadAttachmentFile,
} from "../../store/slice/attachmentsSlice";
import { Messages, Status, Icons, ColumnType } from "../../utils/constants";
import { IFileDetails } from "../../interface/files";
import { REIProgressSpinner, REIToastContainer } from "../ui";
import REIProgressbar from "../ui/progressbar";

interface Props {
  propertyData: string | null;
}

const AdvancedFileUpload: React.FC<Props> = ({ propertyData }) => {
  const [totalSize, setTotalSize] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshData, setRefreshData] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const toastRef = useRef<Toast>(null);
  const fileUploadRef = useRef<any>(null);
  const dispatch: AppDispatch = useDispatch();
  const fetchAttachmentState = useSelector((state: RootState) => state.attachments.fetchAttachmentFiles);
  const uploadAttachmentState = useSelector((state: RootState) => state.attachments.uploadAttachmentResponse);
  const deleteAttachmentState = useSelector((state: RootState) => state.attachments.deleteAttachmentsResponse);
  const watchListID = useRef<string | null>();
  const [fileData, setFileData] = useState<IFileDetails[]>();
  const [uploadKey, setUploadKey] = useState(0);

  const allowedTypes = [
    "image/jpg",
    "image/png",
    "image/gif",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
  ];

  useEffect(() => {
    watchListID.current = propertyData;
    dispatch(resetFileUploadState());
    dispatch(resteDeleteFileState());
  }, [propertyData]);

  const getFiles = async () => {
    setLoading(true);
    await dispatch(
      fetchAttachmentFiles({
        WatchlistID: watchListID.current,
        isAttachementData: true,
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    if (watchListID.current != "") {
      getFiles();
    }
    setProgress(0);
    fileUploadRef.current?.clear();
  }, [dispatch, watchListID.current, refreshData]);

  useEffect(() => {
    if (fetchAttachmentState.status === Status.SUCCESS && fetchAttachmentState.error === "") {
      setFileData(fetchAttachmentState.data);
      setLoading(false);
    } else if (fetchAttachmentState.status === Status.FAILED) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: Messages.ERROR,
        life: 2000,
      });
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [fetchAttachmentState.data, fetchAttachmentState.error]);

  useEffect(() => {
    if (uploadAttachmentState.status === Status.SUCCESS && uploadAttachmentState.error === "") {
      setProgress(100);
      setTimeout(() => {
        setTotalSize(0);
        setLoading(false);
        toastRef.current?.show({
          severity: "success",
          summary: Messages.SUCCESS,
          detail: Messages.FILEUPLOAD_SUCCESS,
          life: 2000,
        });
        setSelectedFiles([]);
        dispatch(resetFileUploadState());
        setRefreshData(!refreshData);
        setUploadKey((prev) => prev + 1);
      }, 1000);
    }

    if (uploadAttachmentState.error !== "") {
      setProgress(0);
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: uploadAttachmentState.error,
        life: 2000,
      });

      setLoading(false);
      setSelectedFiles([]);
      dispatch(resetFileUploadState());
      setRefreshData(!refreshData);
    }
  }, [uploadAttachmentState.data]);

  useEffect(() => {
    if (deleteAttachmentState.status === Status.SUCCESS && deleteAttachmentState.error === "") {
      setLoading(false);
      toastRef.current?.show({
        severity: "success",
        summary: Messages.SUCCESS,
        detail: Messages.FILEDELETE_SUCCESS,
        life: 2000,
      });
      dispatch(resteDeleteFileState());
      setRefreshData(!refreshData);
    }

    if (deleteAttachmentState.error !== "" && deleteAttachmentState.status === Status.FAILED) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: deleteAttachmentState.error,
        life: 3000,
      });

      setSelectedFiles([]);
      dispatch(resteDeleteFileState());
      setRefreshData(!refreshData);
      setLoading(false);
    }
  }, [deleteAttachmentState.data]);

  const deleteSelectedFile = async (file: IFileDetails) => {
    setLoading(true);
    await dispatch(
      deleteAttachmentFile({
        FileName: file.FileName,
        WatchlistID: watchListID.current,
      })
    );
  };

  const handleDeleteDocuments = (event: React.MouseEvent<HTMLElement>, row: IFileDetails) => {
    confirmPopup({
      target: event.currentTarget,
      message: Messages.CONFIRMATION_MESSAGE,
      icon: Icons.INFO_ICON,
      accept: () => deleteSelectedFile(row),
    });
  };

  const fileColumns = [
    {
      field: "FileName",
      header: ColumnType.attachmentFiles,
      icon: "",
      type: ColumnType.link,
    },
    {
      field: "",
      header: "",
      type: ColumnType.custom,
      onClick: handleDeleteDocuments,
      width: "50px",
      template: <i className="pi pi-minus-circle text-[#4E9FBA] cursor-pointer text-sm" title="Delete"></i>,
    },
  ];

  const fileUploader = async (file: File, memberPropertyId: string) => {
    setProgress(20);
    setTotalSize(0);
    setLoading(true);

    const APIparameters = {
      file,
      memberPropertyId,
      isAttachment: 1,
    };

    await dispatch(uploadAttachmentFile(APIparameters));
  };

  const fileUploadHandler = async ({ files }) => {
    if (!validateFileType) return;
    if (!files?.length) return;
    const [file] = files;

    setLoading(true);
    setProgress(10);
    await fileUploader(file, watchListID.current ?? "");
  };

  const validateFileType = (files) => {
    const allowedExtensions = [".jpg", ".png", ".gif", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];
    const maxSizeInBytes = 1.94 * 1024 * 1024;

    const invalidFiles = files.filter((file) => {
      const extension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
      return !allowedExtensions.includes(extension) || file.size > maxSizeInBytes;
    });

    if (invalidFiles.length > 0) {
      const overSizeFiles = invalidFiles.filter((f) => f.size > maxSizeInBytes).map((f) => f.name);
      const invalidTypeFiles = invalidFiles
        .filter((f) => {
          const ext = f.name.toLowerCase().substring(f.name.lastIndexOf("."));
          return !allowedExtensions.includes(ext);
        })
        .map((f) => f.name);

      const errorMessages = [];
      if (invalidTypeFiles.length > 0) {
        errorMessages.push(`Invalid file type(s): ${invalidTypeFiles.join(", ")}`);
      }
      if (overSizeFiles.length > 0) {
        errorMessages.push(`File(s) exceed 1.94 MB: ${overSizeFiles.join(", ")}`);
      }
      toastRef.current?.show({
        severity: "error",
        summary: "Invalid File",
        detail: errorMessages.join(" | "),
        life: 3000,
      });
      fileUploadRef.current?.clear();
      setSelectedFiles([]);
      setTotalSize(0);
      setProgress(0);
      return false;
    }
    return true;
  };

  const onTemplateSelect = (e) => {
    if (validateFileType(e.files)) {
      let _totalSize = totalSize;
      e.files.forEach((file) => (_totalSize += file.size || 0));
      setTotalSize(_totalSize);
      setSelectedFiles(e.files);
    } else {
      setSelectedFiles(() => []);
      fileUploadRef.current?.clear();
      e.originalEvent.target.value = "";
      setUploadKey((prev) => prev + 1);
    }
  };

  const onCancel = () => {
    fileUploadRef.current?.clear();
  };

  const onTemplateRemove = (file, callback) => {
    setTotalSize(totalSize - file.size);
    setSelectedFiles([]);
    callback();
  };

  const itemTemplate = (file, props) => (
    <>
      {loading ? (
        <div className="flex items-center">
          <REIProgressSpinner />
        </div>
      ) : (
        <div className="flex align-items-center flex-wrap self-center">
          <div className="flex space-x-2 justify-between align-items-center">
            <span>
              <img alt={file.name} src={file.objectURL} width={100} />
            </span>
            <span className="text-left ml-3">{file.name}</span>
            <Tag value={props.formatSize} severity="warning" className="px-3 py-2 h-1/2 text-xs" />
            <Button
              type="button"
              icon="pi pi-times"
              className="p-button-outlined p-button-rounded p-button-danger text-xs"
              onClick={() => onTemplateRemove(file, props.onRemove)}
              title="Clear"
            />
          </div>
        </div>
      )}
    </>
  );

  const emptyTemplate = () => (
    <div className="text-center">
      <div className="flex items-center justify-center gap-4">
        <i className="pi pi-image p-5 text-6xl bg-gray-200 text-gray-50 rounded-full"></i>
        <div className="text-2xl text-gray-300">Drag & Drop</div>
      </div>
      <div className="my-5 text-xl text-gray-300">Allowed file types: .JPG, .PNG, .PDF, .DOCX, etc.</div>
      <div className="my-5 text-xl text-gray-300">Max file size: 1.94 MB</div>
    </div>
  );

  const chooseOptions = {
    icon: "pi pi-fw pi-images",
    iconOnly: true,
    className: "custom-choose-btn p-button-rounded p-button-outlined",
  };

  const uploadOptions = {
    icon: "pi pi-fw pi-cloud-upload",
    iconOnly: true,
    className: `custom-upload-btn p-button-success p-button-rounded p-button-outlined ${
      selectedFiles.length === 0 ? "opacity-50 pointer-events-none" : ""
    }`,
  };

  const cancelOptions = {
    icon: "pi pi-fw pi-times",
    iconOnly: true,
    className: `custom-cancel-btn p-button-danger p-button-rounded p-button-outlined text-xs ${
      selectedFiles.length === 0 ? "opacity-50 pointer-events-none" : ""
    }`,
  };

  const headerTemplate = (options) => (
    <>
      <div className={`${options.className} flex content-center text-xs bg-transparent`}>
        {options.chooseButton}
        {options.uploadButton}
        {options.cancelButton}
      </div>
      {progress > 0 && (
        <div className="flex items-center gap-3 ml-auto">
          <REIProgressbar className="w-full h-1 rounded-none" value={progress} showValue={false} mode="determinate" />
        </div>
      )}
    </>
  );

  const progressBarTemplate = () =>
    progress === 0 ? null : (
      <div className="flex align-items-center justify-content-between">
        <REIProgressbar value={progress} showValue={false} mode="determinate" className={""} />
      </div>
    );

  return (
    <>
      <Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
      <Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />
      <Tooltip target=".custom-cancel-btn" content="Clear" position="bottom" />
      <REIToastContainer toastReference={toastRef} />

      <FileUploadWithCustomTemplate
        key={uploadKey}
        accept={allowedTypes.join(",")}
        uploadHandler={fileUploadHandler}
        onSelect={onTemplateSelect}
        onError={onCancel}
        onClear={onCancel}
        onBeforeSelect={() => setProgress(0)}
        headerTemplate={headerTemplate}
        itemTemplate={itemTemplate}
        emptyTemplate={emptyTemplate}
        chooseOptions={chooseOptions}
        uploadOptions={uploadOptions}
        cancelOptions={cancelOptions}
        totalSize={totalSize}
        loading={loading}
        fileData={fileData}
        maxFileSize={9586980}
        fileColumns={fileColumns}
        progressBarTemplate={progressBarTemplate}
      />
    </>
  );
};

export default AdvancedFileUpload;
